/**
 * Booking Calendar Component
 * Based on: documentation/cursor/owner-dashboard-ui-specification-v1-final.md
 * Section 4, Component 9: Booking Calendar
 */

import React, { useState, useEffect, useRef } from 'react';
import { CalendarPlus } from 'lucide-react';
import '../../styles/chart-card.css';
import '../../styles/booking-pill.css';
import { getSourceColorHex, getSourceLabel, getSourceChannelDisplay } from '../../utils/sourceColors.js';
import { resolveMasterChannel } from '../../utils/masterChannelResolver.js';
import { parseLocalDate } from '../../utils/parseLocalDate.js';
import { formatDateOnlyET } from '../../utils/dateTimeET.js';
import { resolveCanonicalDates, calculateMonthOverlap, calculateWeekSegments } from '../../utils/calendarSegmentsET.js';
import { isOwnerStayStatus } from '../../utils/hostawayStatus.js';
import { getTextColorForBackground, getTextColorAndWeightForBackground, getChannelColorToken, getChannelColorHex, getChannelColorVar } from '../../utils/channelColorUtils.js';

const OWNER_STAY_BASE_COLOR_RGB = '216, 207, 196'; // #D8CFC4, same owner channel base color.

export default function BookingCalendar({ bookings = [], channels = [], month, monthStart, monthEnd, onBookingClick, onDateClick, onMonthChange, isCollapsed = false, onToggleCollapse = null, onOwnerBookingClick = null, isMobile = false, selectedDate = null, calendarView = 'month', onViewChange = null, availability = {}, hiddenAvailabilityDates = new Set() }) {
  // State for calendar navigation
  const [currentMonth, setCurrentMonth] = useState(month || new Date());
  const calendarRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  
  // Sync internal state when month prop changes (from MonthSelector Apply or Today)
  useEffect(() => {
    if (month && month instanceof Date && !isNaN(month.getTime())) {
      // Normalize to first day of month for comparison
      const propMonthStart = new Date(month.getFullYear(), month.getMonth(), 1);
      const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      
      // Only update if prop changed (avoid infinite loops)
      if (propMonthStart.getTime() !== currentMonthStart.getTime()) {
        setCurrentMonth(propMonthStart);
      }
    }
  }, [month, currentMonth]);
  
  // Determine month to display
  const displayDate = currentMonth;
  const year = displayDate.getFullYear();
  const monthIndex = displayDate.getMonth();
  
  // Navigation handlers
  const handlePrevMonth = () => {
    const newDate = new Date(year, monthIndex - 1, 1);
    setCurrentMonth(newDate);
    if (onMonthChange) onMonthChange(newDate);
  };
  
  const handleNextMonth = () => {
    const newDate = new Date(year, monthIndex + 1, 1);
    setCurrentMonth(newDate);
    if (onMonthChange) onMonthChange(newDate);
  };
  
  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    if (onMonthChange) onMonthChange(today);
  };

  // Swipe gesture handlers for mobile
  useEffect(() => {
    const calendarElement = calendarRef.current;
    if (!calendarElement) return;

    const handleTouchStart = (e) => {
      // FIX: Don't capture touch if it's on an event pill or "+N more" button
      const target = e.target;
      const isEventPill = target.closest('.booking-span') || target.closest('[role="button"][aria-label*="more booking"]');
      if (isEventPill) {
        // Let the event pill handle the touch
        touchStartX.current = null;
        return;
      }
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      // FIX: Only track if we started on a non-interactive area
      if (touchStartX.current === null) return;
      touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      if (touchStartX.current == null || touchEndX.current == null) {
        touchStartX.current = null;
        touchEndX.current = null;
        return;
      }

      const swipeDistance = touchStartX.current - touchEndX.current;
      const minSwipeDistance = 50; // Minimum distance for a swipe

      if (Math.abs(swipeDistance) > minSwipeDistance) {
        if (swipeDistance > 0) {
          // Swipe left - next month
          handleNextMonth();
        } else {
          // Swipe right - previous month
          handlePrevMonth();
        }
      }

      // Reset
      touchStartX.current = null;
      touchEndX.current = null;
    };

    // FIX: iPadOS system gestures (edge swipe-back, multi-touch, Safari toolbar
    // animations) can abort a touch sequence without ever firing touchend. Without
    // this, touchStartX/touchEndX are left holding stale coordinates from that
    // aborted gesture, and the next unrelated tap gets combined with the leftover
    // value into a bogus "swipe" that silently jumps the calendar a month.
    const handleTouchCancel = () => {
      touchStartX.current = null;
      touchEndX.current = null;
    };

    calendarElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    calendarElement.addEventListener('touchmove', handleTouchMove, { passive: true });
    calendarElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    calendarElement.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      calendarElement.removeEventListener('touchstart', handleTouchStart);
      calendarElement.removeEventListener('touchmove', handleTouchMove);
      calendarElement.removeEventListener('touchend', handleTouchEnd);
      calendarElement.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [currentMonth]);

  // Get first day of month and number of days
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

  // Get month name
  const monthName = displayDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Calculate month bounds (use provided or calculate from month)
  // IMPORTANT: Use provided monthStart/monthEnd if available to ensure consistency with CalendarPage filtering
  const monthStartDate = monthStart ? new Date(monthStart.getTime()) : new Date(year, monthIndex, 1);
  const monthEndDate = monthEnd ? new Date(monthEnd.getTime()) : new Date(year, monthIndex + 1, 0);
  monthStartDate.setHours(0, 0, 0, 0);
  monthEndDate.setHours(23, 59, 59, 999);
  
  // Convert to YYYY-MM-DD for date-only comparisons (no timezone issues)
  const monthStartYmd = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
  const monthEndYmd = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  
  // Debug flag (gated by ?perf_debug=1)
  const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';

  // MASTER CHANNEL COLOR MAPPING (CSS tokens as single source of truth)
  // Always use CSS variables - do not rely on backend-provided hex values to avoid drift
  // This ensures legend and pills always match
  // STRICT: Must pass booking status and canonical to properly detect Owner stays
  const getSourceColor = (channelCode, booking = null) => {
    // Extract booking metadata for proper master channel resolution
    const bookingStatus = booking?.status || null;
    const canonical = booking?.canonical_financial_summary || null;
    const clcChannels = booking?.clc_channels || null;
    
    // Resolve master channel with full context (status, canonical, clcChannels)
    // This ensures Owner stays are properly detected via status check
    const masterChannel = resolveMasterChannel(
      channelCode,
      null, // channelSubtype (not needed, canonical has it)
      bookingStatus, // status - REQUIRED for Owner stay detection
      clcChannels, // clcChannels - for forced lease detection
      canonical // canonical - preferred source for forced lease
    );
    
    // Use strict CSS variable resolver (single source of truth) - shared with legend
    return getChannelColorToken(masterChannel.master_channel_key);
  };
  
  // Get text color for a given background color (auto-selects white or dark based on luminance)
  const getPillTextColor = (backgroundColor) => {
    return getTextColorForBackground(backgroundColor);
  };
  
  // Get text color and font-weight based on contrast (Option 3: contrast-based, not channel-based)
  // Returns both textColor and fontWeight based on the same luminance calculation
  const getPillTextColorAndWeight = (backgroundColor) => {
    return getTextColorAndWeightForBackground(backgroundColor);
  };

  /**
   * Format currency without cents (rounded to nearest dollar)
   * @param {number} value - Currency value
   * @returns {string|null} Formatted currency string (e.g., "$18,850") or null if invalid
   */
  const formatCurrencyNoCents = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    // Round to nearest dollar
    const rounded = Math.round(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rounded);
  };

  /**
   * Get secondary text for calendar pill label
   * Returns "Owner Stay" for owner stays, formatted payout for normal reservations, or fallback
   * NOTE: Forced lease (clc_channels="CLC - Lease") overrides OwnerStay status
   * @param {Object} booking - Reservation/booking object
   * @returns {string} Secondary text to display after guest name
   */
  const getCalendarPillSecondaryText = (booking) => {
    if (booking?.hide_calendar_pill_secondary_text) {
      return isOwnerStayStatus(booking?.status) ? 'Owner Stay' : null;
    }

    if (booking?.calendar_pill_secondary_text) {
      return booking.calendar_pill_secondary_text;
    }

    // Check canonical for forced lease FIRST (forced lease wins over OwnerStay)
    const canonical = booking?.canonical_financial_summary;
    const isForcedLease = canonical?.channel_subtype === 'clc_lease' || 
                          canonical?.payout_method === 'clc_lease_computed';
    
    // If forced lease, show payout (not "Owner Stay")
    if (isForcedLease) {
      const ownerPayout = canonical?.owner_payout?.owner_payout_total || 
                         canonical?.owner_payout?.owner_estimated_payout;
      
      if (ownerPayout !== null && ownerPayout !== undefined && !isNaN(ownerPayout)) {
        const formatted = formatCurrencyNoCents(ownerPayout);
        if (formatted) {
          return formatted;
        }
      }
      return '—';
    }
    
    // Owner Stay override (only if NOT forced lease)
    if (isOwnerStayStatus(booking?.status)) {
      return 'Owner Stay';
    }
    
    // Get owner payout from canonical financial summary
    const ownerPayout = canonical?.owner_payout?.owner_payout_total || 
                       canonical?.owner_payout?.owner_estimated_payout;
    
    if (ownerPayout !== null && ownerPayout !== undefined && !isNaN(ownerPayout)) {
      const formatted = formatCurrencyNoCents(ownerPayout);
      if (formatted) {
        return formatted;
      }
    }
    
    // Fallback if payout is missing/undefined
    return '—';
  };

  /**
   * Get day number style (moved outside map to avoid TDZ)
   * @param {boolean} isMobile - Whether mobile view
   * @param {boolean} daySelected - Whether day is selected
   * @returns {Object} Style object
   */
  const getDayNumberStyle = (isMobile, daySelected) => {
    const selected = daySelected === true;
    return {
      fontSize: isMobile ? 'var(--font-size-sm)' : 'var(--font-size-sm)',
      fontWeight: isMobile && selected 
        ? 'var(--font-weight-bold)' // PHASE 9D: Bold for selected day (hierarchy)
        : 'var(--font-weight-medium)',
      color: isMobile && selected 
        ? 'var(--color-brand-primary, #2563eb)' // PHASE 9D: Brand color for selected (hierarchy)
        : 'var(--color-text-primary)',
      flexShrink: 0,
      lineHeight: '1.2',
      // PHASE 9D: Smooth transition for weight/color changes
      transition: 'font-weight 120ms ease-out, color 120ms ease-out',
      // Ensure day number renders above blocked overlay (no overlap)
      position: 'relative',
      zIndex: 2,
      top: '-3px',
    };
  };

  /**
   * Calculate booking spans for the month, split by week
   * Uses canonical segmentation utility for correct date-only parsing and overlap detection
   * Returns spans grouped by week, with grid column positions
   */
  const calculateBookingSpans = () => {
    const allSpans = [];
    
    bookings.forEach(booking => {
      // Resolve canonical dates (handles aliases: check_in_date, checkin_date, etc.)
      const { checkinYmd, checkoutYmd } = resolveCanonicalDates(booking);
      
      if (!checkinYmd || !checkoutYmd) {
        if (perfDebug) {
          console.log('[Calendar] Skipping booking - missing dates:', {
            reservation_id: booking.reservation_id,
            guest_name: booking.guest_name,
            checkinYmd,
            checkoutYmd,
            raw_fields: {
              check_in_date: booking.check_in_date,
              checkin_date: booking.checkin_date,
              check_out_date: booking.check_out_date,
              checkout_date: booking.checkout_date,
            },
          });
        }
        return; // Skip invalid dates
      }
      
      // Calculate month overlap using date-only comparisons (YYYY-MM-DD strings)
      const { overlaps, displayStartYmd, displayEndYmd } = calculateMonthOverlap(
        checkinYmd,
        checkoutYmd,
        monthStartYmd,
        monthEndYmd
      );
      
      if (!overlaps || !displayStartYmd || !displayEndYmd) {
        if (perfDebug) {
          console.log('[Calendar] Booking does not overlap month:', {
            reservation_id: booking.reservation_id,
            guest_name: booking.guest_name,
            checkinYmd,
            checkoutYmd,
            monthStartYmd,
            monthEndYmd,
            overlaps,
          });
        }
        return; // Booking does not overlap this month
      }
      
      // Debug logging (gated by ?perf_debug=1)
      if (perfDebug) {
        console.log('[Calendar] Processing booking:', {
          reservation_id: booking.reservation_id,
          guest_name: booking.guest_name,
          checkinYmd,
          checkoutYmd,
          displayStartYmd,
          displayEndYmd,
          monthStartYmd,
          monthEndYmd,
        });
      }
      
      // Calculate week segments (handles week boundary splits)
      const segments = calculateWeekSegments(
        displayStartYmd,
        displayEndYmd,
        monthStartYmd,
        startDayOfWeek
      );
      
      if (perfDebug && segments.length > 0) {
        console.log('[Calendar] Week segments:', {
          reservation_id: booking.reservation_id,
          guest_name: booking.guest_name,
          segments: segments.map(s => ({
            weekNumber: s.weekNumber,
            gridStartIndex: s.gridStartIndex,
            gridEndIndex: s.gridEndIndex,
            spanDays: s.spanDays,
            dayOfMonth: s.dayOfMonth,
          })),
        });
      }
      
      // Convert segments to span objects
      // Debug logging for troubleshooting (gated by perf_debug)
      if (perfDebug && segments.length === 0 && overlaps && displayStartYmd && displayEndYmd) {
        console.warn('[Calendar] No segments created but overlap exists:', {
          reservation_id: booking.reservation_id,
          guest_name: booking.guest_name,
          displayStartYmd,
          displayEndYmd,
          monthStartYmd,
        });
      }
      
      segments.forEach((segment, segmentIdx) => {
        allSpans.push({
          booking,
          weekNumber: segment.weekNumber,
          gridStartIndex: segment.gridStartIndex,
          gridEndIndex: segment.gridEndIndex,
          dayOfWeek: segment.dayOfWeek,
          spanDays: segment.spanDays,
          dayOfMonth: segment.dayOfMonth,
          channelCode: booking.channel || booking.channel_code,
          segmentIndex: segmentIdx,
          segmentTotal: segments.length,
        });
      });
    });
    
    // Group spans by week and assign lanes
    const spansByWeek = {};
    allSpans.forEach(span => {
      if (!spansByWeek[span.weekNumber]) {
        spansByWeek[span.weekNumber] = [];
      }
      spansByWeek[span.weekNumber].push(span);
    });
    
    // Assign lanes for overlapping spans - PER DAY, not per week
    // This ensures overlapping events on the same day stack vertically within that day cell
    Object.keys(spansByWeek).forEach(weekNum => {
      const weekSpans = spansByWeek[weekNum];
      
      // Group spans by day (gridStartIndex to gridEndIndex represents days)
      const spansByDay = {};
      weekSpans.forEach(span => {
        for (let dayIdx = span.gridStartIndex; dayIdx <= span.gridEndIndex; dayIdx++) {
          if (!spansByDay[dayIdx]) {
            spansByDay[dayIdx] = [];
          }
          spansByDay[dayIdx].push(span);
        }
      });
      
      // Assign lanes per day: for each day, assign lanes to overlapping spans
      Object.keys(spansByDay).forEach(dayIdx => {
        const daySpans = spansByDay[dayIdx];
        // Sort by start index (earlier starts get lower lanes)
        daySpans.sort((a, b) => a.gridStartIndex - b.gridStartIndex);
        
        daySpans.forEach((span, idx) => {
          // Find the minimum lane that doesn't conflict with other spans on this day
          let lane = 0;
          while (daySpans.slice(0, idx).some(other => {
            // Check if spans overlap on this specific day
            const otherStart = Math.max(span.gridStartIndex, other.gridStartIndex);
            const otherEnd = Math.min(span.gridEndIndex, other.gridEndIndex);
            return other.lane === lane && otherStart <= otherEnd;
          })) {
            lane++;
          }
          // Use the maximum lane across all days this span covers
          span.lane = Math.max(span.lane || 0, lane);
        });
      });
    });
    
    return spansByWeek;
  };

  const spansByWeek = calculateBookingSpans();

  // LEGEND DATA SOURCE - STRICTLY DYNAMIC, BASED ON VISIBLE PILLS ONLY
  // Build the legend channel set from the same spans used to render booking pills.
  // CRITICAL: Must pass booking status and canonical to resolveMasterChannel to properly detect Owner stays
  // (matches getSourceColor pattern - same data source ensures legend matches pill colors)
  const visibleChannelKeySet = new Set();

  Object.values(spansByWeek || {}).forEach((weekSpans) => {
    (weekSpans || []).forEach((span) => {
      if (!span) return;
      const code =
        span.channelCode ||
        span.booking?.channel ||
        span.booking?.source ||
        '';
      if (!code) return;

      // Extract booking metadata for proper master channel resolution (same as getSourceColor)
      const bookingStatus = span.booking?.status || null;
      const canonical = span.booking?.canonical_financial_summary || null;
      const clcChannels = span.booking?.clc_channels || null;
      
      // Resolve master channel with full context (status, canonical, clcChannels)
      // This ensures Owner stays are properly detected via status check
      const master = resolveMasterChannel(
        code,
        null, // channelSubtype (not needed, canonical has it)
        bookingStatus, // status - REQUIRED for Owner stay detection
        clcChannels, // clcChannels - for forced lease detection
        canonical // canonical - preferred source for forced lease
      );
      const key = (master?.master_channel_key || code || '').toLowerCase().trim();
      if (key) {
        visibleChannelKeySet.add(key);
      }
    });
  });

  // Convert to ordered array for legend rendering
  const preferredOrder = ['vrbo', 'airbnb', 'clc', 'owner'];
  const allVisibleKeys = Array.from(visibleChannelKeySet);

  const orderedPreferred = preferredOrder.filter((k) => visibleChannelKeySet.has(k));
  const extraKeys = allVisibleKeys
    .filter((k) => !preferredOrder.includes(k))
    .sort();

  const visibleLegendKeys = [...orderedPreferred, ...extraKeys];
  
  // Calculate number of weeks needed
  const totalDays = startDayOfWeek + daysInMonth;
  const numberOfWeeks = Math.ceil(totalDays / 7);

  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push({ day: null, date: null, bookings: [] });
  }
  
  // Helper function to format date as YYYY-MM-DD using local date components (no timezone conversion)
  const formatLocalDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIndex, day);
    const dateKey = formatLocalDateKey(date); // Use local date components, not UTC
    calendarDays.push({
      day,
      date,
      dateKey,
      bookings: [], // Not used with span-based rendering, kept for compatibility
    });
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if blocked days exist in current visible month
  const hasBlockedDays = availability && typeof availability === 'object' && 
    Object.values(availability).some(dayData => dayData && dayData.status === 'BLOCKED');

  // Always render the calendar grid, even with zero bookings
  // No empty-state message - grid renders normally with empty cells
  return (
    <div ref={calendarRef} className="chart-card owner-dashboard-calendar" style={styles.getCardStyle(isMobile)}>
      {/* Unified Calendar Toolbar - PHASE 2: Stacked on mobile, PHASE 6A: Hide nav on mobile */}
      <div style={styles.getCalendarToolbarStyle(isMobile)} className="calendar-toolbar" data-testid="calendar-toolbar">
        {/* MOBILE PRO: Month/Week toggle (mobile only) */}
        {isMobile && onViewChange && (
          <div style={styles.viewToggle} role="group" aria-label="Calendar view">
            <button
              onClick={() => onViewChange('month')}
              style={{
                ...styles.viewToggleButton,
                ...(calendarView === 'month' ? styles.viewToggleButtonActive : {}),
              }}
              data-view-toggle="month"
              aria-label="View: Month"
              aria-pressed={calendarView === 'month'}
            >
              Month
            </button>
            <button
              onClick={() => onViewChange('week')}
              style={{
                ...styles.viewToggleButton,
                ...(calendarView === 'week' ? styles.viewToggleButtonActive : {}),
              }}
              data-view-toggle="week"
              aria-label="View: Week"
              aria-pressed={calendarView === 'week'}
            >
              Week
            </button>
          </div>
        )}
        {/* Left: Month/Year - PHASE 6A: Hidden on mobile (command bar has it) */}
        {!isMobile && (
          <div style={styles.toolbarLeft} className="toolbar-left">
            <h3 style={styles.toolbarTitle}>{monthName}</h3>
          </div>
        )}
        {/* Middle: Calendar Legend - Desktop only, inline with month label */}
        {!isMobile && !isCollapsed && (visibleLegendKeys.length > 0 || hasBlockedDays) && (
          <div
            style={styles.getLegendContainerStyle(isMobile, true)}
            className="calendar-legend-container"
            data-testid="calendar-legend"
            data-debug-visible-legend-keys={visibleLegendKeys.join(',')}
          >
            <div style={styles.getToolbarLegendStyle(isMobile)} className="calendar-toolbar-legend">
              {visibleLegendKeys.map((key, idx) => {
                const master = resolveMasterChannel(key, null, null);
                const label = master?.master_channel_label || key.toUpperCase();
                const color = getChannelColorVar(master || { master_channel_key: key });

                return (
                  <div
                    key={key || idx}
                    style={styles.legendItem}
                    className="legend-item"
                  >
                    <div style={{ ...styles.legendColor, backgroundColor: color }} />
                    <span style={styles.legendLabel}>{label}</span>
                    {(idx < visibleLegendKeys.length - 1 || hasBlockedDays) && (
                      <span style={styles.legendSeparator}>•</span>
                    )}
                  </div>
                );
              })}
              {/* Lock legend - only show when blocked days exist */}
              {hasBlockedDays && (
                <div style={styles.legendItem} className="legend-item">
                  <div style={styles.legendLockIcon}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3V12a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
                        clipRule="evenodd"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <span style={styles.legendLabel}>Not yet open for booking</span>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Right: Navigation - PHASE 6A: Hidden on mobile (command bar has prev/next) */}
        {!isMobile && (
          <div style={styles.toolbarNav} className="toolbar-nav">
            <button 
              onClick={handlePrevMonth}
              style={styles.navButton}
              aria-label="Previous month"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              }}
              onFocus={(e) => {
                Object.assign(e.currentTarget.style, styles.navButtonFocus);
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
              }}
            >
              ←
            </button>
            <button 
              onClick={handleToday}
              style={styles.navButton}
              aria-label="Today"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              }}
              onFocus={(e) => {
                Object.assign(e.currentTarget.style, styles.navButtonFocus);
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
              }}
            >
              Today
            </button>
            <button 
              onClick={handleNextMonth}
              style={styles.navButton}
              aria-label="Next month"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              }}
              onFocus={(e) => {
                Object.assign(e.currentTarget.style, styles.navButtonFocus);
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
              }}
            >
              →
            </button>
            {/* PHASE 6A: "Hide calendar" moved to card header on mobile (see below) */}
            {onOwnerBookingClick && (
              <button
                onClick={onOwnerBookingClick}
                style={styles.ownerBookingButton}
                aria-label="Book owner stay"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-brand-primary-hover, #1d4ed8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-brand-primary, #2563eb)';
                }}
                onFocus={(e) => {
                  Object.assign(e.currentTarget.style, styles.navButtonFocus);
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none';
                }}
              >
                <CalendarPlus size={15} strokeWidth={2.4} aria-hidden="true" />
                <span>Owner Booking</span>
              </button>
            )}
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                style={styles.collapseButton}
                aria-label={isCollapsed ? 'Show calendar' : 'Hide calendar'}
                aria-expanded={!isCollapsed}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                }}
                onFocus={(e) => {
                  Object.assign(e.currentTarget.style, styles.navButtonFocus);
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none';
                }}
              >
                {isCollapsed ? '▼ Show calendar' : '▲ Hide calendar'}
              </button>
            )}
          </div>
        )}
        {/* PHASE 6B: "Hide calendar" moved to BottomSheet on mobile (removed from toolbar) */}
      </div>
      {!isCollapsed && (
        <>
          {/* Mobile Legend - Below toolbar, above grid (mobile only) */}
          {isMobile && (
            <div
              style={styles.getLegendContainerStyle(isMobile, false)}
              className="calendar-legend-container"
              data-testid="calendar-legend"
              data-debug-visible-legend-keys={visibleLegendKeys.join(',')}
            >
              <div style={styles.getToolbarLegendStyle(isMobile)} className="calendar-toolbar-legend">
                {visibleLegendKeys.map((key, idx) => {
                  // For mobile legend, resolve master channel from key (already resolved in visibleLegendKeys)
                  // Use a dummy booking lookup to get proper label/color if needed, but key is already master_channel_key
                  const masterChannel = resolveMasterChannel(key, null, null);
                  const displayText = masterChannel?.master_channel_label || key.toUpperCase();
                  const sourceColor = getChannelColorVar(masterChannel);
                  
                  return (
                    <div
                      key={key || idx}
                      style={styles.legendItem}
                      className="legend-item"
                    >
                      <div style={{ ...styles.legendColor, backgroundColor: sourceColor }} />
                      <span style={styles.legendLabel}>{displayText}</span>
                      {(idx < visibleLegendKeys.length - 1 || hasBlockedDays) && (
                        <span style={styles.legendSeparator}>•</span>
                      )}
                    </div>
                  );
                })}
                {hasBlockedDays && (
                  <div style={styles.legendItem} className="legend-item">
                    <div style={styles.legendLockIcon}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3V12a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" fill="currentColor"/>
                      </svg>
                    </div>
                    <span style={styles.legendLabel}>Not yet open for booking</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="calendarGrid" style={{ ...styles.getCalendarGridStyle(isMobile, numberOfWeeks), position: 'relative' }}>
        {/* Day Headers - PHASE 7A: Mobile-responsive styling */}
        {weekDays.map((day, idx) => (
          <div 
            key={day} 
            style={{
              ...styles.getDayHeaderStyle(isMobile),
              borderRight: idx === 6 ? 'none' : '1px solid var(--color-border-light)',
            }}
          >
            {day}
          </div>
        ))}
        {/* Calendar Days and Week Overlays */}
        {Array.from({ length: numberOfWeeks }).map((_, weekIndex) => {
          const weekStartDayIndex = weekIndex * 7;
          const weekDays = calendarDays.slice(weekStartDayIndex, weekStartDayIndex + 7);
          const weekSpans = spansByWeek[weekIndex] || [];
          
          return (
            <React.Fragment key={`week-${weekIndex}`}>
              {/* Day Cells */}
              {weekDays.map((dayData, dayInWeek) => {
                const dayIndex = weekStartDayIndex + dayInWeek;
                const isLastInWeek = dayInWeek === 6;
                const hasSpans = weekSpans.some(span => 
                  span.gridStartIndex <= dayIndex && span.gridEndIndex >= dayIndex
                );
                const hasOwnerStaySpans = weekSpans.some(span =>
                  span.gridStartIndex <= dayIndex &&
                  span.gridEndIndex >= dayIndex &&
                  isOwnerStayStatus(span.booking?.status)
                );
                
                // PHASE 8: Check if this day is selected
                // PHASE 9D: Compute selected state - use helper function to avoid TDZ in closures
                // Use dateKey (local date components) for comparison, not UTC
                const dayDateStr = dayData.dateKey || (dayData.date ? formatLocalDateKey(dayData.date) : null);
                const isHiddenAvailabilityDate = Boolean(dayDateStr && hiddenAvailabilityDates?.has?.(dayDateStr));
                const selectedDateStr = selectedDate 
                  ? (selectedDate instanceof Date ? formatLocalDateKey(selectedDate) : (typeof selectedDate === 'string' ? selectedDate.split('T')[0] : null))
                  : null;
                // Helper function to compute selected state (avoids TDZ in closures)
                const computeIsSelected = () => dayDateStr && selectedDateStr && dayDateStr === selectedDateStr;
                const dayIsSelected = computeIsSelected();

                return (
                  <div
                    key={dayIndex}
                    style={{
                      ...styles.getCalendarDayStyle(isMobile),
                      ...(dayIsSelected && isMobile ? styles.calendarDaySelected : {}),
                      borderRight: isLastInWeek ? 'none' : '1px solid var(--color-border-light)',
                      cursor: dayData.day !== null && !hasSpans && !isHiddenAvailabilityDate && onDateClick ? 'pointer' : 'default',
                      position: 'relative',
                    }}
                    onClick={() => {
                      if (dayData.day !== null && !hasSpans && !isHiddenAvailabilityDate && onDateClick) {
                        onDateClick(dayData.date, dayData.dateKey);
                      }
                    }}
                    // PHASE 9D: Touch feedback - active state (mobile only)
                    onTouchStart={(e) => {
                      if (isMobile && dayData.day !== null && !hasSpans && !isHiddenAvailabilityDate && onDateClick) {
                        e.currentTarget.style.transform = 'scale(0.98)';
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-hover, #f3f4f6)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (isMobile) {
                        e.currentTarget.style.transform = '';
                        const selected = computeIsSelected();
                        e.currentTarget.style.backgroundColor = selected ? 'var(--color-bg-secondary, #f3f4f6)' : 'transparent';
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile && dayData.day !== null && !hasSpans && !isHiddenAvailabilityDate) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile && dayData.day !== null && !hasSpans && !isHiddenAvailabilityDate) {
                        const selected = computeIsSelected();
                        e.currentTarget.style.backgroundColor = selected ? 'var(--color-bg-secondary, #f3f4f6)' : 'transparent';
                      }
                    }}
                  >
                    {dayData.day !== null && (
                      <>
                        <div style={styles.dayNumberContainer}>
                          <div style={getDayNumberStyle(isMobile, dayIsSelected)}>{dayData.day}</div>
                        </div>
                        {/* Hostaway Availability Overlay - Show on open days and under owner stays */}
                        {(!hasSpans || hasOwnerStaySpans) && dayData.dateKey && availability && typeof availability === 'object' && availability[dayData.dateKey] && (() => {
                          // DEBUG: Log date key matching (gated by ?perf_debug=1)
                          const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';
                          if (perfDebug) {
                            // Log all days for debugging overlay rendering
                            console.log('[Calendar Overlay Debug]', {
                              day: dayData.day,
                              dateKey: dayData.dateKey,
                              date: dayData.date,
                              dateISO: dayData.date ? dayData.date.toISOString() : null,
                              availabilityKeys: availability ? Object.keys(availability).slice(0, 10) : 'NO AVAILABILITY',
                              availabilityCount: availability ? Object.keys(availability).length : 0,
                              hasMatch: !!(availability && availability[dayData.dateKey]),
                              matchData: availability ? availability[dayData.dateKey] : null,
                              availabilityType: typeof availability,
                              availabilityIsObject: availability && typeof availability === 'object',
                              hasSpans: hasSpans,
                            });
                          }
                          
                          const availData = availability[dayData.dateKey];
                          const status = availData.status; // 'AVAILABLE' or 'BLOCKED'
                          // Convert to numbers if strings (API may return strings)
                          const nightlyRate = typeof availData.nightly_rate === 'string' 
                            ? parseFloat(availData.nightly_rate) 
                            : availData.nightly_rate;
                          const minNights = typeof availData.min_nights === 'string'
                            ? parseInt(availData.min_nights, 10)
                            : availData.min_nights;
                          
                          // Format nightly rate (match existing currency formatting)
                          const formattedRate = nightlyRate !== null && nightlyRate !== undefined && !isNaN(nightlyRate)
                            ? formatCurrencyNoCents(nightlyRate)
                            : null;
                          
                          // Only render if status is AVAILABLE or BLOCKED
                          if (status !== 'AVAILABLE' && status !== 'BLOCKED') {
                            return null;
                          }
                          
                          const isBlocked = status === 'BLOCKED';
                          const isUnderOwnerStay = hasSpans && hasOwnerStaySpans;
                          const isInteractiveEmptyDay = !hasSpans && !isHiddenAvailabilityDate && onDateClick;
                          
                          return (
                            <div 
                              style={{
                                ...styles.availabilityOverlay,
                                ...(isInteractiveEmptyDay ? styles.emptyDayClickable : {}),
                                ...(isBlocked ? styles.blockedOverlay : {}),
                                ...(isUnderOwnerStay ? styles.availabilityOverlayUnderOwnerStay : {}),
                              }}
                              title={`${isBlocked && !isUnderOwnerStay ? 'Not yet open for booking • ' : ''}${formattedRate || 'N/A'}${minNights ? ` • ${minNights} nights min` : ''}`}
                              onClick={(e) => {
                                if (isInteractiveEmptyDay) {
                                  e.stopPropagation();
                                  onDateClick(dayData.date, dayData.dateKey);
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (isInteractiveEmptyDay) {
                                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isInteractiveEmptyDay) {
                                  e.currentTarget.style.backgroundColor = isBlocked ? 'rgba(107, 114, 128, 0.10)' : 'var(--color-bg-secondary)';
                                }
                              }}
                              tabIndex={isInteractiveEmptyDay ? 0 : undefined}
                              role={isInteractiveEmptyDay ? "button" : undefined}
                              aria-label={isInteractiveEmptyDay ? `${isBlocked ? 'Not yet open for booking' : 'Available date'} ${dayData.dateKey ? formatDateOnlyET(dayData.dateKey, { monthDay: true }) : 'N/A'}${formattedRate ? `, ${formattedRate}` : ''}${minNights ? `, ${minNights} nights minimum` : ''}, click to book owner stay` : undefined}
                              onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && isInteractiveEmptyDay) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onDateClick(dayData.date, dayData.dateKey);
                                }
                              }}
                              onFocus={(e) => {
                                if (isInteractiveEmptyDay) {
                                  e.currentTarget.style.outline = 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)';
                                  e.currentTarget.style.outlineOffset = 'var(--focus-outline-offset)';
                                }
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.outline = 'none';
                              }}
                            >
                              {/* Blocked indicator - lock icon only (moved down, no label) */}
                              {isBlocked && !isUnderOwnerStay && (
                                <div style={styles.availabilityLockIcon} title="Not yet open for booking">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3V12a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" fill="currentColor"/>
                                  </svg>
                                </div>
                              )}
                              {/* Price pill - MUST show for both AVAILABLE and BLOCKED */}
                              {formattedRate && (
                                <div style={styles.availabilityRate}>{formattedRate}</div>
                              )}
                              {/* Min nights pill - MUST show for both AVAILABLE and BLOCKED */}
                              {minNights && (
                                <div style={styles.availabilityMinNights}>{minNights} nights min</div>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Show "Available" when no spans and no availability overlay (same on mobile and desktop) */}
                        {!hasSpans && !isHiddenAvailabilityDate && (!dayData.dateKey || !availability || !availability[dayData.dateKey]) && (
                          <div 
                            style={{
                              ...styles.emptyDayHint,
                              ...(onDateClick ? styles.emptyDayClickable : {}),
                            }}
                            title="Book owner stay"
                            onClick={(e) => {
                              if (onDateClick) {
                                e.stopPropagation();
                                onDateClick(dayData.date, dayData.dateKey);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (onDateClick) {
                                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (onDateClick) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                            tabIndex={onDateClick ? 0 : undefined}
                            role={onDateClick ? "button" : undefined}
                            aria-label={onDateClick ? `Available date ${dayData.dateKey ? formatDateOnlyET(dayData.dateKey, { monthDay: true }) : 'N/A'}, click to book owner stay` : undefined}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && onDateClick) {
                                e.preventDefault();
                                e.stopPropagation();
                                onDateClick(dayData.date, dayData.dateKey);
                              }
                            }}
                            onFocus={(e) => {
                              if (onDateClick) {
                                e.currentTarget.style.outline = 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)';
                                e.currentTarget.style.outlineOffset = 'var(--focus-outline-offset)';
                              }
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.outline = 'none';
                            }}
                          >
                            Available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {/* Week Overlay for Spans - positioned absolutely over the week row */}
              {weekSpans.length > 0 && (
                <div
                  key={`overlay-${weekIndex}`}
                  style={{
                    ...styles.weekOverlay,
                    gridColumn: '1 / -1',
                    gridRow: weekIndex + 2, // +2 because row 1 is headers
                  }}
                >
                  {(() => {
                    // Calculate max lanes needed for this week
                    const maxLanes = Math.max(0, ...weekSpans.map(s => s.lane || 0)) + 1;
                    const PILL_HEIGHT = 22;
                    const LANE_GAP = 3;
                    const ROW_HEIGHT = PILL_HEIGHT + LANE_GAP;
                    
                    // Same functionality on mobile and desktop: show multiple lanes (up to maxVisibleLanes), then "+N more"
                    const rowHeightPx = 100; // Same row height for both so both get 2 lanes
                    const maxVisibleLanes = Math.floor((rowHeightPx - 44) / ROW_HEIGHT);
                    
                    // Group spans by day to show "+N more" per day
                    const spansByDay = {};
                    weekSpans.forEach(span => {
                      const weekStartGridIndex = weekIndex * 7;
                      for (let dayIdx = span.gridStartIndex; dayIdx <= span.gridEndIndex; dayIdx++) {
                        const dayInWeek = dayIdx - weekStartGridIndex;
                        if (dayInWeek >= 0 && dayInWeek < 7) {
                          if (!spansByDay[dayInWeek]) {
                            spansByDay[dayInWeek] = [];
                          }
                          if (!spansByDay[dayInWeek].includes(span)) {
                            spansByDay[dayInWeek].push(span);
                          }
                        }
                      }
                    });
                    
                    return (
                      <>
                        {weekSpans.map((span, spanIdx) => {
                          // Same rule for both: show up to maxVisibleLanes
                          if (span.lane >= maxVisibleLanes) {
                            return null;
                          }
                          // Calculate position within the week (0-6 for days of week)
                          const weekStartGridIndex = weekIndex * 7;
                          const spanStartInWeek = span.gridStartIndex - weekStartGridIndex;
                          const spanEndInWeek = span.gridEndIndex - weekStartGridIndex;
                          
                          // Calculate grid column positions (1-indexed for CSS grid)
                          const startCol = spanStartInWeek + 1;
                          const endCol = spanEndInWeek + 2; // +2 because grid-column-end is exclusive
                          
                          // Get background color and contrast-based text color + font-weight (Option 3)
                          const backgroundColor = getSourceColor(span.channelCode, span.booking);
                          const { textColor, fontWeight } = getPillTextColorAndWeight(backgroundColor);
                          const isOwnerStaySpan = isOwnerStayStatus(span.booking?.status);
                          const pillBackgroundColor = isOwnerStaySpan
                            ? `rgba(${OWNER_STAY_BASE_COLOR_RGB}, 0.42)`
                            : backgroundColor;
                          const pillTextColor = isOwnerStaySpan ? '#111827' : textColor;
                          const pillFontWeight = isOwnerStaySpan ? 'var(--font-weight-medium)' : fontWeight;
                          const pillBoxShadow = isOwnerStaySpan
                            ? '0 1px 2px rgba(15, 23, 42, 0.12)'
                            : '0 1px 3px rgba(0, 0, 0, 0.25)';
                          const pillTextShadow = isOwnerStaySpan ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.3)';
                          // Note: fontWeight is contrast-based (white text -> 500, dark text -> 400), NOT channel-based
                          // Continuation cues: same reservation spans multiple week rows
                          const segmentTotal = span.segmentTotal ?? 1;
                          const isContinuation = (span.segmentIndex ?? 0) > 0;
                          const continuesNext = (span.segmentIndex ?? 0) < segmentTotal - 1;

                          const handlePillClick = (e) => {
                            // Click anywhere on pill opens modal (including padding areas)
                            e.stopPropagation();
                            if (onBookingClick) {
                              // Debug logging (gated by ?perf_debug=1)
                              const urlParams = new URLSearchParams(window.location.search);
                              if (urlParams.get('perf_debug') === '1') {
                                console.log('[Calendar pill click]', {
                                  reservation_id: span.booking.reservation_id,
                                  guest_name: span.booking.guest_name,
                                  clickTarget: e.target.tagName,
                                  clickCurrentTarget: e.currentTarget.tagName,
                                  boundingRect: e.currentTarget.getBoundingClientRect(),
                                });
                              }
                              onBookingClick(span.booking);
                            }
                          };

                          // Same pill structure on mobile and desktop: one pill per lane, same layout and interaction
                          return (
                            <div
                              key={`${span.booking.reservation_id}-${spanIdx}`}
                              className="booking-span booking-pill"
                              data-calendar-event="true"
                              data-calendar-chip="true"
                              data-reservation-id={span.booking.reservation_id}
                              style={{
                                ...styles.bookingSpan,
                                backgroundColor: pillBackgroundColor,
                                color: pillTextColor,
                                gridColumn: `${startCol} / ${endCol}`,
                                gridRow: span.lane + 1,
                                pointerEvents: 'auto',
                                cursor: 'pointer',
                                touchAction: 'manipulation',
                                boxShadow: pillBoxShadow,
                                textShadow: pillTextShadow,
                                ...(isContinuation && !isOwnerStaySpan && { borderLeft: '3px solid rgba(0,0,0,0.2)' }),
                                ...(continuesNext && !isOwnerStaySpan && { borderRight: '2px solid rgba(0,0,0,0.15)' }),
                              }}
                              onClick={handlePillClick}
                              onMouseEnter={(e) => {
                                if (isOwnerStaySpan) {
                                  e.currentTarget.style.backgroundColor = `rgba(${OWNER_STAY_BASE_COLOR_RGB}, 0.58)`;
                                } else {
                                  e.currentTarget.style.opacity = '0.9';
                                }
                                e.currentTarget.style.outline = '2px solid rgba(0, 0, 0, 0.35)';
                                e.currentTarget.style.outlineOffset = '-2px';
                              }}
                              onMouseLeave={(e) => {
                                if (isOwnerStaySpan) {
                                  e.currentTarget.style.backgroundColor = pillBackgroundColor;
                                } else {
                                  e.currentTarget.style.opacity = '1';
                                }
                                e.currentTarget.style.outline = 'none';
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={`Booking for ${span.booking.guest_name || 'Guest'}${getCalendarPillSecondaryText(span.booking) ? `, ${getCalendarPillSecondaryText(span.booking)}` : ''}, click to view details`}
                              title={`${span.booking.guest_name || 'Guest'}${getCalendarPillSecondaryText(span.booking) ? ` • ${getCalendarPillSecondaryText(span.booking)}` : ''}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onBookingClick && onBookingClick(span.booking);
                                }
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.outline = 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)';
                                e.currentTarget.style.outlineOffset = 'var(--focus-outline-offset)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.outline = 'none';
                              }}
                            >
                              <span
                                style={{
                                  ...styles.spanContent,
                                  pointerEvents: 'none',
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontWeight: pillFontWeight,
                                }}
                              >
                                {`${span.booking.guest_name || 'Guest'}${getCalendarPillSecondaryText(span.booking) ? ` • ${getCalendarPillSecondaryText(span.booking)}` : ''}`}
                                {continuesNext && <span style={{ marginLeft: '4px', opacity: 0.9 }}>›</span>}
                              </span>
                            </div>
                          );
                  }).filter(Boolean)}
                        {/* "+N more" when there are more bookings than visible lanes (same on mobile and desktop) */}
                        {Object.keys(spansByDay).map(dayInWeek => {
                          const daySpans = spansByDay[dayInWeek];
                          const hiddenCount = daySpans.filter(s => s.lane >= maxVisibleLanes).length;
                          if (hiddenCount > 0) {
                            // Calculate the date for this day in the week
                            const weekStartDate = new Date(year, monthIndex, 1);
                            const firstDayOfWeek = weekStartDate.getDay();
                            const weekStart = new Date(weekStartDate);
                            weekStart.setDate(weekStart.getDate() - firstDayOfWeek);
                            const dayDate = new Date(weekStart);
                            dayDate.setDate(weekStart.getDate() + (weekIndex * 7) + parseInt(dayInWeek));
                            
                            return (
                              <div
                                key={`more-${weekIndex}-${dayInWeek}`}
                                style={{
                                  gridColumn: `${dayInWeek + 1} / ${dayInWeek + 2}`,
                                  gridRow: maxVisibleLanes + 1,
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-text-link, #2563eb)', // WORLD-CLASS: Clear link color
                                  padding: '6px 8px', // WORLD-CLASS: Better touch target
                                  minHeight: '44px', // Ensure 44px touch target
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'transparent', // No background (link style)
                                  border: 'none', // No border (link style)
                                  borderRadius: 'var(--radius-sm)',
                                  textAlign: 'center',
                                  pointerEvents: 'auto',
                                  cursor: 'pointer',
                                  zIndex: 21,
                                  fontWeight: 'var(--font-weight-medium)', // WORLD-CLASS: Medium weight for readability
                                  textDecoration: 'underline', // Underline for link affordance
                                  textDecorationColor: 'var(--color-text-link, #2563eb)',
                                  textUnderlineOffset: '3px', // WORLD-CLASS: Slightly more offset
                                  touchAction: 'manipulation', // Prevent scroll conflicts
                                }}
                                data-day-more="true"
                                data-calendar-day={`${weekIndex}-${dayInWeek}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // On mobile, trigger date click to show day details
                                  if (onDateClick) {
                                    onDateClick(dayDate, formatDateOnlyET(dayDate.toISOString().split('T')[0]));
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if ((e.key === 'Enter' || e.key === ' ') && onDateClick) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onDateClick(dayDate, formatDateOnlyET(dayDate.toISOString().split('T')[0]));
                                  }
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`${hiddenCount} more booking${hiddenCount > 1 ? 's' : ''} on this day, click to view`}
                                title={`${hiddenCount} more booking${hiddenCount > 1 ? 's' : ''} on this day`}
                              >
                                +{hiddenCount} more
                              </div>
                            );
                          }
                          return null;
                        }).filter(Boolean)}
                      </>
                    );
                  })()}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

const styles = {
  // PHASE 12: Card style - mobile removes all chrome, desktop keeps it
  getCardStyle: (isMobile) => ({
    backgroundColor: isMobile ? 'transparent' : 'var(--color-bg-primary)', // PHASE 12: Transparent on mobile
    border: isMobile ? 'none' : '1px solid var(--color-border-light)', // PHASE 12: No border on mobile
    borderRadius: isMobile ? 0 : 'var(--radius-lg)', // PHASE 12: No radius on mobile
    padding: isMobile ? '8px 0' : 'var(--spacing-5)', // PHASE 12: Minimal padding on mobile
    boxShadow: isMobile ? 'none' : 'var(--shadow-md)', // PHASE 12: No shadow on mobile
  }),
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-3)',
    marginBottom: 'var(--spacing-4)',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 'var(--spacing-2)',
  },
  navigation: {
    display: 'flex',
    gap: 'var(--spacing-2)',
    alignItems: 'center',
  },
  navButton: {
    padding: 'var(--spacing-1) var(--spacing-3)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
  },
  navButtonFocus: {
    outline: 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)',
    outlineOffset: 'var(--focus-outline-offset)',
  },
  collapseButton: {
    padding: 'var(--spacing-1) var(--spacing-3)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    whiteSpace: 'nowrap',
  },
  ownerBookingButton: {
    padding: 'var(--spacing-1) var(--spacing-3)',
    backgroundColor: 'var(--color-brand-primary, #2563eb)',
    border: '1px solid var(--color-brand-primary, #2563eb)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  emptyDayHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    fontStyle: 'italic',
    marginTop: 'var(--spacing-1)',
    padding: 'var(--spacing-1)',
    opacity: 0.6,
    textAlign: 'center',
    // PHASE 7A: Hide on mobile (emptyDayHint is hidden via conditional rendering)
  },
  title: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  legend: {
    display: 'flex',
    gap: 'var(--spacing-4)',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)', /* Tighter gap for compact horizontal layout */
    whiteSpace: 'nowrap',
  },
  legendColor: {
    width: '10px', /* Smaller dot for compactness */
    height: '10px',
    borderRadius: '50%', /* Perfect circle */
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: 'var(--font-size-xs)', /* Smaller text for density */
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-medium)',
  },
  legendSeparator: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    margin: '0 var(--spacing-1)',
    userSelect: 'none',
  },
  // PHASE 12: Calendar grid - mobile removes nested card look
  // FIX: Calendar row height consistency - explicit row sizing for uniform week rows
  getCalendarGridStyle: (isMobile, numberOfWeeks) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gridTemplateRows: isMobile 
      ? `auto repeat(${numberOfWeeks}, 80px)`
      : `auto repeat(${numberOfWeeks}, 100px)`,
    border: isMobile ? '1px solid var(--color-border-light)' : '1px solid var(--color-border-medium)',
    borderRadius: isMobile ? 0 : 'var(--radius-md)',
    overflow: isMobile ? 'hidden' : 'visible',
    overflowX: isMobile ? 'hidden' : undefined,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    position: 'relative',
    boxShadow: 'none',
    boxSizing: 'border-box',
  }),
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', /* Equal columns, prevent overflow */
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    overflow: 'visible', /* Allow spans to extend */
    width: '100%',
    position: 'relative',
  },
  weekOverlay: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gridAutoRows: '25px', /* Fixed row height: 22px pill + 3px gap = 25px per lane */
    position: 'absolute',
    top: '44px', /* Start below reserved day number area (40px min-height + 4px buffer) */
    left: 0, /* Align with calendar grid boundaries */
    right: 0, /* Align with calendar grid boundaries */
    bottom: 0, /* Extend to bottom of day cells */
    pointerEvents: 'none', /* Allow clicks to pass through to day cells - individual spans have pointerEvents: auto */
    rowGap: '3px', /* Gap between lanes: 3px */
    columnGap: 'var(--spacing-1)', /* Gap between day columns */
    alignContent: 'start',
    overflowX: 'hidden', /* Clip spans that extend horizontally beyond their grid column boundaries */
    overflowY: 'hidden', /* Clip vertically to prevent pills from exceeding day cell height */
    padding: '0 var(--spacing-1)', /* Small horizontal padding to match day cell padding */
    zIndex: 15, /* FIX: Ensure overlay is above day cells (z-index: 10) but below global nav */
  },
  // PHASE 7A: Mobile-responsive day header (reduced visual weight on mobile)
  // PHASE 8: Optimized density - tighter padding
  // PHASE 9B: Day headers lighter weight on mobile (grid is overview, not primary)
  getDayHeaderStyle: (isMobile) => ({
    padding: isMobile ? '6px 4px' : 'var(--spacing-3)', // PHASE 11: 6px/4px on mobile (was 8px/16px)
    backgroundColor: 'var(--color-bg-tertiary)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: isMobile ? 'var(--font-weight-normal)' : 'var(--font-weight-semibold)', // PHASE 9B: Normal weight on mobile (lighter)
    color: isMobile ? 'var(--color-text-tertiary)' : 'var(--color-text-secondary)', // PHASE 9B: Tertiary color on mobile (lighter)
    textAlign: 'center',
    borderBottom: isMobile ? '1px solid var(--color-border-light)' : '1px solid var(--color-border-medium)', // PHASE 9B: Lighter borders on mobile
    minHeight: isMobile ? '32px' : 'auto', // PHASE 11: Compact header on mobile
  }),
  // PHASE 9D: Month grid - Same row height on mobile and desktop for same lane count
  getCalendarDayStyle: (isMobile) => ({
    minHeight: '100px',
    height: '100px',
    padding: isMobile ? '4px 6px' : 'var(--spacing-2)', // PHASE 11: Tighter padding (4px vertical, 6px horizontal)
    paddingTop: isMobile ? '1px' : 'calc(var(--spacing-3) - 6px)', // PHASE 11: Minimal top padding (reduced further to anchor day numbers near top-left)
    borderBottom: isMobile ? '1px solid var(--color-border-light)' : '1px solid var(--color-border-medium)', // PHASE 9B: Lighter borders on mobile (overview)
    backgroundColor: 'var(--color-bg-primary)',
    position: 'relative',
    // PHASE 9D: Smooth transitions for all state changes (touch feedback, selection)
    transition: isMobile 
      ? 'background-color 80ms ease-out, transform 80ms ease-out, opacity 120ms ease-out' 
      : 'background-color 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden', /* Clip content that extends horizontally beyond day cell boundaries (prevents pill bleed into adjacent cells) */
    overflowY: 'hidden', /* FIX: Clip vertically to prevent content from exceeding fixed row height (defensive overflow fallback) */
    minWidth: 0, /* Allow flex shrinking */
    // PHASE 9D: Touch feedback - subtle scale on active (mobile only)
    ...(isMobile ? {
      WebkitTapHighlightColor: 'transparent', // Remove default tap highlight
    } : {}),
  }),
  calendarDay: {
    minHeight: '100px', /* Consistent minimum height */
    padding: 'var(--spacing-2)',
    paddingTop: 'var(--spacing-3)', /* Extra top padding for day number */
    borderBottom: '1px solid var(--color-border-medium)',
    backgroundColor: 'var(--color-bg-primary)',
    position: 'relative',
    transition: 'background-color 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible', /* Allow spans and overlays to extend/position correctly */
    minWidth: 0, /* Allow flex shrinking */
    overflowX: 'hidden', /* Clip horizontal overflow to prevent bleed */
    overflowY: 'visible', /* Allow vertical overflow for absolute positioned overlays */
  },
  dayNumberContainer: {
    minHeight: '40px', /* Reserved space: padding-top (12px) + day number (~18px) + margin (4px) + buffer (6px) */
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    zIndex: 10, /* Ensure day number area is above booking bars */
  },
  spansContainer: {
    position: 'relative',
    flex: 1,
    minHeight: '60px',
    width: '100%',
  },
  bookingSpan: {
    minHeight: '22px', // Desktop: Reduced to fit 2 lanes
    height: '22px', // Desktop: Fixed height for consistent lane stacking
    padding: '4px var(--spacing-2)', // Desktop: Reduced vertical padding
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    lineHeight: '14px', // Desktop: Tighter line height
    whiteSpace: 'nowrap', // Prevent multi-line wrapping
    overflow: 'hidden', // Hide overflow
    textOverflow: 'ellipsis', // Show ellipsis for long text
    display: 'flex', // Use flex for alignment
    marginLeft: '3px', // Visual inset: prevents pill from touching left cell boundary
    marginRight: '3px', // Visual inset: prevents pill from touching right cell boundary
    alignItems: 'center', // Center vertically
    // Color is set dynamically based on background luminance (auto-selects white or dark)
    // Font weight is NOT set here - it's set on the label span based on contrast (Option 3)
    // This ensures white text uses 500, dark text uses 400 (contrast-based, not channel-based)
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    flexShrink: 0,
    position: 'relative', // Ensure z-index works
    zIndex: 20, // Above day cells (dayNumberContainer z-index: 10) and overlays
    pointerEvents: 'auto', // Ensure clicks work
    touchAction: 'manipulation', // Prevent scroll from canceling taps
    boxSizing: 'border-box', // Include padding in width calculation
    maxWidth: '100%', // Ensure span respects its grid column boundaries
    // WORLD-CLASS V2: Mobile compact chip - CSS will override these via .booking-span class
  },
  spanContent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  /* Remove right border from last column */
  'calendarDay:last-child, dayHeader:last-child': {
    borderRight: 'none',
  },
  dayNumber: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    flexShrink: 0, /* Never shrink */
    lineHeight: '1.2', /* Consistent line height */
  },
  bookingPill: {
    padding: 'var(--spacing-1) var(--spacing-2)', /* Reduced padding to fit fixed height */
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)', /* Slightly smaller to fit */
    color: '#ffffff', /* Ensure white text for contrast */
    fontWeight: 'var(--font-weight-semibold)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    height: '28px', /* Fixed height - never changes */
    maxHeight: '28px', /* Constrained */
    lineHeight: '1.2',
    position: 'relative',
    flexShrink: 0, /* Never shrink */
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
  },
  taxIndicator: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-status-warning-base, #f59e0b)',
    lineHeight: 1,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  moreBookings: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-bg-tertiary)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    textAlign: 'center',
    height: '24px', /* Fixed height */
    maxHeight: '24px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyDayClickable: {
    cursor: 'pointer',
  },
  // PHASE 9D: Selected day - Enhanced visual weight (hierarchy via contrast, not spacing)
  calendarDaySelected: {
    backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
    border: '2px solid var(--color-brand-primary, #2563eb)',
    borderRadius: 'var(--radius-sm)',
    // PHASE 9D: Stronger contrast for selected state (feels anchored)
    boxShadow: '0 0 0 1px rgba(37, 99, 235, 0.1)', // PHASE 9D: Subtle glow for emphasis
  },
  emptyState: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },
  // PHASE 2: Mobile toolbar - stacked layout
  // UPDATED: Desktop uses 3-zone flex (left: month, middle: legend, right: nav)
  getCalendarToolbarStyle: (isMobile) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row', // PHASE 2: Stack on mobile
    alignItems: isMobile ? 'stretch' : 'center', // PHASE 2: Stretch on mobile
    justifyContent: isMobile ? 'flex-start' : 'space-between', // Desktop: space-between for 3 zones
    gap: isMobile ? 'var(--spacing-2)' : 'var(--spacing-3)', // PHASE 2: Tighter gap on mobile
    marginBottom: 'var(--spacing-4)',
    paddingBottom: 'var(--spacing-3)',
    borderBottom: '1px solid var(--color-border-light)',
    // PHASE 2: No wrapping on desktop, natural stack order on mobile (month → nav → legend)
    flexWrap: isMobile ? 'nowrap' : 'nowrap', // Desktop: single row, no wrap
  }),
  toolbarLeft: {
    flex: '0 0 auto',
    minWidth: 0,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  toolbarTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // RESTORED: Legend container style - positioned inline with header (desktop) or above grid (mobile)
  // isHeaderMode: true when legend is in toolbar row, false when standalone above grid
  getLegendContainerStyle: (isMobile, isHeaderMode = false) => ({
    padding: isHeaderMode ? 0 : (isMobile ? 'var(--spacing-2) 0' : 'var(--spacing-3) 0'),
    marginBottom: isHeaderMode ? 0 : 'var(--spacing-2)',
    borderBottom: isHeaderMode ? 'none' : '1px solid var(--color-border-light)',
    width: isHeaderMode ? 'auto' : '100%',
    flex: isHeaderMode ? '1 1 auto' : 'none',
    minWidth: isHeaderMode ? 0 : 'auto',
    display: isHeaderMode ? 'flex' : 'block',
    alignItems: isHeaderMode ? 'center' : 'flex-start',
    justifyContent: isHeaderMode ? 'center' : 'flex-start',
  }),
  // PHASE 2: Mobile toolbar legend - full width when stacked
  // UPDATED: Desktop header mode uses flex:1 to fill middle zone, allows wrap on narrow widths
  getToolbarLegendStyle: (isMobile) => ({
    display: 'flex',
    flex: '0 0 auto', // Don't grow the inner legend flex container itself
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center', // Center legend items within their container
    gap: 'var(--spacing-4)',
    flexWrap: 'wrap', // Allow wrapping on narrow widths to prevent overflow
    whiteSpace: 'nowrap',
    width: 'auto',
  }),
  toolbarNav: {
    display: 'flex',
    flex: '0 0 auto',
    gap: 'var(--spacing-2)',
    alignItems: 'center',
    flexShrink: 0,
  },
  // PHASE 6A: Mobile "Hide calendar" container
  mobileCollapseContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingTop: 'var(--spacing-2)',
  },
  mobileCollapseButton: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    whiteSpace: 'nowrap',
    minHeight: '32px', // Touch target minimum
  },
  // Hostaway Availability Overlay
  availabilityOverlay: {
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    right: '4px',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'normal',
    padding: '4px 6px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-sm)',
    zIndex: 1, // Below day number (zIndex: 2) to prevent overlap
    minHeight: 'auto',
    overflow: 'visible',
    pointerEvents: 'auto',
  },
  blockedOverlay: {
    backgroundColor: 'rgba(107, 114, 128, 0.10)',
  },
  availabilityOverlayUnderOwnerStay: {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: '0 2px',
    pointerEvents: 'none',
    zIndex: 30,
  },
  availabilityLockIcon: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary, #6b7280)',
    lineHeight: '1',
    marginTop: '4px',
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '16px',
    height: '16px',
  },
  legendLockIcon: {
    width: '12px',
    height: '12px',
    color: 'var(--color-text-secondary, #6b7280)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: '4px',
  },
  availabilityRate: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  availabilityMinNights: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
    display: 'block',
  },
};
